/**
 * useMatchMedia — subscribes to a CSS media query and returns whether it
 * currently matches.
 *
 * Unlike a `useState` + `useEffect` hook, the first render already reflects the
 * real `matchMedia` result because `useSyncExternalStore` reads the snapshot
 * synchronously. That means a mobile viewport renders its mobile layout on the
 * very first paint instead of flashing the desktop layout for a frame before an
 * effect corrects it. On the server (no `window`) the snapshot is `false`, so
 * hydration starts from the non-matching assumption and the client immediately
 * reconciles to the real value.
 */
import * as React from "react";

function canUseMatchMedia(): boolean {
  return (
    typeof window !== "undefined" && typeof window.matchMedia === "function"
  );
}

export function useMatchMedia(query: string): boolean {
  const subscribe = React.useCallback(
    (callback: () => void) => {
      if (!canUseMatchMedia()) {
        return () => {};
      }
      const mediaQueryList = window.matchMedia(query);
      mediaQueryList.addEventListener("change", callback);
      return () => mediaQueryList.removeEventListener("change", callback);
    },
    [query],
  );

  const getSnapshot = React.useCallback(
    () => canUseMatchMedia() && window.matchMedia(query).matches,
    [query],
  );

  return React.useSyncExternalStore(subscribe, getSnapshot, () => false);
}
